import {
  getMemberNavigationConfig,
  getManagerNavigationConfig,
  NavigationItemConfig,
  NavigationSectionGroup,
} from "~/config/NavigationConfig";

import { NavigationItemCompanyType } from "~/domains";

/**
 * Navigation項目に基づくアクセス制御
 *
 * このモジュールは、navigationItemsのhiddenAtフィールドを使って、
 * 特定のページへのアクセスを制御します。
 *
 * SOLID原則:
 * - SRP: ナビゲーションベースのアクセス制御のみを担当
 * - OCP: 新しいナビゲーション項目への対応が容易
 * - DIP: 設定に依存し、具体的な実装に依存しない
 *
 * テスタビリティ:
 * - 純粋関数なので副作用がなく、単体テストが容易
 * - モックやスタブが不要
 */

/**
 * URLパスがワイルドカードパターンにマッチするかチェック
 *
 * @param path - チェックするURLパス
 * @param pattern - ワイルドカードパターン（例: "/managers/employees/*"）
 * @returns マッチする場合true
 */
const matchesPattern = (path: string, pattern: string): boolean => {
  if (pattern === path) return true;
  if (!pattern.includes("*")) return false;

  const regexPattern = pattern.replace(/\*/g, ".*");
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(path);
};

/**
 * NavigationSectionGroup[]から名前でアイテムを検索
 */
const findItemByName = (
  groups: NavigationSectionGroup[],
  name: string,
): NavigationItemConfig | undefined => {
  for (const group of groups) {
    if (name in group.items) return group.items[name];
  }
  return undefined;
};

/**
 * navigationItemsから非表示（hidden）な項目のnameリストを取得
 *
 * hiddenAtが設定されている項目のみを返します。
 *
 * @param navigationItems - navigation項目の配列
 * @param role - ロール（"member" または "manager"）
 * @returns 非表示項目のnameリスト
 */
const getHiddenNavigationNames = (
  navigationItems: NavigationItemCompanyType[],
  role: "member" | "manager",
): string[] => {
  return navigationItems
    .filter((item) => item.role === role && !!item.hiddenAt)
    .map((item) => item.name);
};

/**
 * 現在のURLパスがアクセス可能かチェック（Member用）
 *
 * @param currentPath - 現在のURLパス
 * @param navigationItems - navigation項目の配列
 * @param defaultBusinessSectionId - デフォルトの事業セクションID
 * @returns アクセス可能な場合true
 */
export const checkMemberNavigationAccess = (
  currentPath: string,
  navigationItems: NavigationItemCompanyType[],
  defaultBusinessSectionId: string,
): boolean => {
  // navigationItemsからhiddenな項目を取得
  const hiddenNavigationNames = getHiddenNavigationNames(
    navigationItems,
    "member",
  );

  // navigationConfigを取得
  const navigationConfig = getMemberNavigationConfig(defaultBusinessSectionId);

  // hiddenな項目に対応するURLパターンを収集
  const hiddenPatterns: string[] = [];
  hiddenNavigationNames.forEach((name) => {
    const config = findItemByName(navigationConfig, name);
    if (config?.hrefPatterns) {
      hiddenPatterns.push(...config.hrefPatterns);
    }
  });

  // 現在のパスがhiddenなパターンにマッチする場合はアクセス不可
  return !hiddenPatterns.some((pattern) =>
    matchesPattern(currentPath, pattern),
  );
};

/**
 * 現在のURLパスがアクセス可能かチェック（Manager用）
 *
 * @param currentPath - 現在のURLパス
 * @param navigationItems - navigation項目の配列
 * @param defaultBusinessSectionId - デフォルトの事業セクションID
 * @returns アクセス可能な場合true
 */
export const checkManagerNavigationAccess = (
  currentPath: string,
  navigationItems: NavigationItemCompanyType[],
  defaultBusinessSectionId: string,
): boolean => {
  const hiddenNavigationNames = getHiddenNavigationNames(
    navigationItems,
    "manager",
  );
  const navigationConfig = getManagerNavigationConfig(defaultBusinessSectionId);

  const hiddenPatterns: string[] = [];
  hiddenNavigationNames.forEach((name) => {
    const config = findItemByName(navigationConfig, name);
    if (config?.hrefPatterns) {
      hiddenPatterns.push(...config.hrefPatterns);
    }
  });

  return !hiddenPatterns.some((pattern) =>
    matchesPattern(currentPath, pattern),
  );
};

/**
 * 現在のURLパスがアクセス可能かチェック（ロール自動判定版）
 *
 * @param currentPath - 現在のURLパス
 * @param navigationItems - navigation項目の配列
 * @param defaultBusinessSectionId - デフォルトの事業セクションID
 * @returns アクセス可能な場合true
 */
export const checkNavigationAccess = (
  currentPath: string,
  navigationItems: NavigationItemCompanyType[],
  defaultBusinessSectionId: string,
): boolean => {
  // Analytics Plansページは常にアクセス可能
  // （プランベースのアクセス制御はCurrentEmployeeProviderで既に実施済み）
  const isAnalyticsPlansPage = currentPath.startsWith("/analytics_plans");
  if (isAnalyticsPlansPage) {
    return true;
  }

  // Managerページかどうかで判定を分ける
  const isManagerPage = currentPath.startsWith("/managers");

  if (isManagerPage) {
    return checkManagerNavigationAccess(
      currentPath,
      navigationItems,
      defaultBusinessSectionId,
    );
  } else {
    return checkMemberNavigationAccess(
      currentPath,
      navigationItems,
      defaultBusinessSectionId,
    );
  }
};
